// The in-memory fake: what a consumer's tests run against. Also the
// reference for what the contract expects of a real provider.
import { entriesUnder, normalizePath, scoped, toText, type FileData, type Storage } from './index.ts';

export class MemoryStorage implements Storage {
  private files = new Map<string, FileData>();

  async read(path: string): Promise<FileData | undefined> {
    return this.files.get(normalizePath(path));
  }

  async readText(path: string): Promise<string | undefined> {
    const data = await this.read(path);
    return data === undefined ? undefined : toText(data);
  }

  async write(path: string, data: FileData): Promise<void> {
    const p = normalizePath(path);
    if (!p) throw new Error('cannot write to the root');
    this.files.set(p, data);
  }

  async exists(path: string): Promise<boolean> {
    return this.files.has(normalizePath(path));
  }

  async list(dir: string): Promise<ReturnType<typeof entriesUnder>> {
    return entriesUnder(dir, this.files.keys());
  }

  async remove(path: string): Promise<void> {
    const p = normalizePath(path);
    if (!p) {
      this.files.clear();
      return;
    }
    this.files.delete(p);
    const prefix = `${p}/`;
    for (const key of [...this.files.keys()]) {
      if (key.startsWith(prefix)) this.files.delete(key);
    }
  }

  scope(dir: string): Storage {
    return scoped(this, dir);
  }
}
